import styles from "./calendar-widget.module.css";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Flex, Segmented, Button, Calendar, Spin } from "antd";

import dayjs from "dayjs";

import Widget from "@/components/ui/Widget";
import DaySchedule from "@/components/DaySchedule";

import CalendarIcon from "@/icons/CalendarIcon";
import ViewListIcon from "@/icons/ViewListIcon";
import ChevronLeftIcon from "@/icons/ChevronLeftIcon";
import ChevronRightIcon from "@/icons/ChevronRightIcon";

import { getPeriodTasks, getNearest } from "@/api/calendar";
import { getRecurringItems as getRecurringTasks } from "@/api/tasks";

import routes from "@/routes";

const formatLocal = (d) => d.format("YYYY-MM-DD[T]HH:mm:ss");

const CalendarWidget = () => {
  const isFirstLoad = useRef(true);
  const dayScrollRef = useRef(null);
  const monthScrollRef = useRef(null);

  const [dayData, setDayData] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [dayPending, setDayPending] = useState(true);
  const [monthPending, setMonthPending] = useState(true);
  const [nearest, setNearest] = useState(null);
  const [selectView, setSelectView] = useState("day");
  const [selectDay, setSelectDay] = useState(new Date());

  const isToday = useMemo(() => {
    const today = dayjs();

    return dayjs(selectDay).isSame(today, "day");
  }, [selectDay]);

  const dayTitle = useMemo(() => {
    const day = String(selectDay.getDate()).padStart(2, "0");
    const month = String(selectDay.getMonth() + 1).padStart(2, "0");
    const year = String(selectDay.getFullYear()).slice(-2);

    return `${isToday ? "Сегодня " : ""}${day}.${month}.${year}`;
  }, [selectDay, isToday]);

  const monthTitle = useMemo(() => {
    return selectDay
      .toLocaleDateString("ru-RU", {
        month: "long",
        year: "numeric",
      })
      .slice(0, -2);
  }, [selectDay]);

  useEffect(() => {
    fetchMonthData(selectDay);
    fetchNearest();

    // eslint-disable-next-line
  }, []);

  const fetchDayData = async (date) => {
    setDayPending(true);

    const { start, end } = getStartEndDay(date);

    const params = {
      start_date: start,
      end_date: end,
    };
    try {
      const [tasks, recurringTasks] = await Promise.all([
        getPeriodTasks(params),
        getRecurringTasks(params),
      ]);

      setDayData([...(tasks || []), ...(recurringTasks || [])]);
    } catch (error) {
      console.log("error", error);
    } finally {
      setDayPending(false);
    }
  };

  const fetchMonthData = async (date) => {
    setMonthPending(true);

    const { start, end } = getStartEndMonth(date);

    const params = {
      start_date: start,
      end_date: end,
    };
    try {
      const [tasks, recurringTasks] = await Promise.all([
        getPeriodTasks(params),
        getRecurringTasks(params),
      ]);

      setMonthData([...(tasks || []), ...(recurringTasks || [])]);

      const dayTasks = tasks.filter((task) => {
        return (
          (task.due_date || task.scheduled_due_date) &&
          dayjs(task.due_date || task.scheduled_due_date).isSame(
            dayjs(selectDay),
            "day"
          )
        );
      });

      setDayData(dayTasks);

      if (isFirstLoad.current && !dayTasks.length) {
        setSelectView("month");

        isFirstLoad.current = false;
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setMonthPending(false);
      setDayPending(false);
    }
  };

  const fetchNearest = async () => {
    try {
      const nearest = await getNearest();
      setNearest(nearest);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getStartEndDay = (date) => ({
    start: formatLocal(dayjs(date).startOf("day")),
    end: formatLocal(dayjs(date).endOf("day")),
  });

  const getStartEndMonth = (date) => ({
    start: formatLocal(dayjs(date).startOf("month")),
    end: formatLocal(dayjs(date).endOf("month")),
  });

  const handleLeftClick = () => {
    const newDate = new Date(selectDay);
    if (selectView === "day") {
      newDate.setDate(newDate.getDate() - 1);
      fetchDayData(newDate);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
      fetchMonthData(newDate);
    }
    setSelectDay(newDate);
  };
  const handleRightClick = () => {
    const newDate = new Date(selectDay);
    if (selectView === "day") {
      newDate.setDate(newDate.getDate() + 1);
      fetchDayData(newDate);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
      fetchMonthData(newDate);
    }
    setSelectDay(newDate);
  };

  const handleDateChange = (date) => {
    if (selectDay.getMonth() !== new Date(date).getMonth()) {
      fetchMonthData(new Date(date));
    } else {
      fetchDayData(new Date(date));
    }

    setSelectDay(new Date(date));
  };

  return (
    <Widget
      value="calendar"
      header={
        <Flex
          align="center"
          justify="space-between"
          className={styles.widget_header}
        >
          <Button
            color="default"
            variant="link"
            size="small"
            icon={<ChevronLeftIcon style={{ fontSize: 18 }} />}
            onClick={() => handleLeftClick()}
          />

          <Link
            to={routes.calendar(
              selectView === "day"
                ? { view: "day", ...getStartEndDay(selectDay) }
                : { view: "month", ...getStartEndMonth(selectDay) }
            )}
            className={styles.title}
          >
            {selectView === "day" ? dayTitle : monthTitle}
          </Link>

          <Button
            color="default"
            variant="link"
            size="small"
            icon={<ChevronRightIcon style={{ fontSize: 18 }} />}
            onClick={() => handleRightClick()}
          />
        </Flex>
      }
      isOpen={true}
      extra={
        <Segmented
          value={selectView}
          options={[
            { value: "day", icon: <ViewListIcon /> },
            { value: "month", icon: <CalendarIcon /> },
          ]}
          onChange={setSelectView}
        />
      }
    >
      <>
        {selectView === "day" ? (
          dayPending ? (
            <div className="spin">
              <Spin size="large" />
            </div>
          ) : (
            <div
              ref={dayScrollRef}
              style={{ height: "100%", overflow: "auto" }}
            >
              <DaySchedule
                day={selectDay}
                data={dayData}
                nearest={nearest}
                statusPlacement="back"
                style={{ paddingRight: 3, marginBottom: 25 }}
                scrollContainerRef={dayScrollRef}
              />
            </div>
          )
        ) : (
          <Flex vertical gap={8} className="h-100">
            <>
              <Spin size="large" spinning={monthPending}>
                <Calendar
                  fullscreen={false}
                  mode="month"
                  headerRender={() => null}
                  value={dayjs(selectDay)}
                  onChange={handleDateChange}
                  cellRender={(date) => {
                    // Проверяем, есть ли задачи на эту дату
                    const hasTasksOnDate = monthData.some((task) => {
                      return dayjs(
                        task.due_date || task.scheduled_due_date
                      ).isSame(dayjs(date), "day");
                    });

                    return hasTasksOnDate ? (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "#F8FBFF",
                          border: "1px solid #D5E2F7",
                          borderRadius: 4,
                          zIndex: -1,
                        }}
                      />
                    ) : null;
                  }}
                />
              </Spin>

              {dayPending ? (
                <div className="spin">
                  <Spin size="large" />
                </div>
              ) : (
                <div
                  ref={monthScrollRef}
                  style={{ height: "100%", overflow: "auto" }}
                >
                  <DaySchedule
                    day={selectDay}
                    data={dayData}
                    nearest={nearest}
                    showEmpty
                    statusPlacement="back"
                    style={{ paddingRight: 3, marginBottom: 25 }}
                    scrollContainerRef={monthScrollRef}
                  />
                </div>
              )}
            </>
          </Flex>
        )}
      </>
    </Widget>
  );
};

export default CalendarWidget;
